import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, FileText, Shield, X } from "lucide-react";
import { Link } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface ConsentStatus {
  needsToS: boolean;
  needsAdminToS: boolean;
  needsPrivacy: boolean;
}

export function ConsentBanner() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [acceptedToS, setAcceptedToS] = useState(false);
  const [acceptedAdminToS, setAcceptedAdminToS] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  // Check if user needs to re-consent
  const { data: needsReconsent } = useQuery<ConsentStatus>({
    queryKey: ['/api/legal/consent-status'],
    enabled: !!user,
    queryFn: async () => {
      const response = await fetch('/api/legal/consent-status', {
        credentials: 'include',
      });
      if (!response.ok) return { needsToS: false, needsAdminToS: false, needsPrivacy: false };
      return await response.json();
    },
    retry: false,
  });

  const updateConsentMutation = useMutation({
    mutationFn: async (data: { acceptedToS: boolean; acceptedAdminToS: boolean; acceptedPrivacy: boolean }) => {
      return await apiRequest('POST', '/api/legal/update-consent', data);
    },
    onSuccess: () => {
      toast({
        title: "Consent Updated",
        description: "Your consent has been updated successfully.",
      });
      setIsDismissed(true);
      // Refetch consent status
      window.location.reload();
    },
    onError: (error: any) => {
      toast({
        title: "Update Failed",
        description: error.message || "Failed to update consent. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleAccept = () => {
    if (needsReconsent?.needsToS && !acceptedToS) {
      toast({
        title: "Consent Required",
        description: "You must accept the Terms of Service to continue.",
        variant: "destructive",
      });
      return;
    }
    if (needsReconsent?.needsAdminToS && !acceptedAdminToS) {
      toast({
        title: "Consent Required",
        description: "You must accept the Administrator Terms of Service to continue.",
        variant: "destructive",
      });
      return;
    }
    if (needsReconsent?.needsPrivacy && !acceptedPrivacy) {
      toast({
        title: "Consent Required",
        description: "You must accept the Privacy Policy to continue.",
        variant: "destructive",
      });
      return;
    }

    updateConsentMutation.mutate({
      acceptedToS: acceptedToS || !needsReconsent?.needsToS,
      acceptedAdminToS: acceptedAdminToS || !needsReconsent?.needsAdminToS,
      acceptedPrivacy: acceptedPrivacy || !needsReconsent?.needsPrivacy,
    });
  };

  // Don't show if user is not authenticated or doesn't need reconsent
  if (!user || !needsReconsent || (!needsReconsent.needsToS && !needsReconsent.needsAdminToS && !needsReconsent.needsPrivacy) || isDismissed) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-background border-t border-border shadow-lg">
      <Card className="max-w-4xl mx-auto">
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-amber-500" />
              <div>
                <CardTitle>Legal Documents Updated</CardTitle>
                <CardDescription>
                  Our Terms of Service and/or Privacy Policy have been updated. Please review and accept to continue.
                </CardDescription>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsDismissed(true)}
              className="h-8 w-8"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              You must accept the updated legal documents to continue using Dugout Desk.
            </AlertDescription>
          </Alert>

          <div className="space-y-4">
            {needsReconsent.needsToS && (
              <div className="flex items-start gap-3">
                <Checkbox
                  id="tos-reconsent"
                  checked={acceptedToS}
                  onCheckedChange={(checked) => setAcceptedToS(checked === true)}
                  className="mt-1"
                />
                <Label htmlFor="tos-reconsent" className="text-sm leading-relaxed cursor-pointer flex-1">
                  I accept the updated{" "}
                  <Link href="/legal/terms" className="text-primary hover:underline" target="_blank">
                    Terms of Service
                  </Link>
                  {" "}*
                </Label>
              </div>
            )}

            {needsReconsent.needsAdminToS && (
              <div className="flex items-start gap-3">
                <Checkbox
                  id="tos-admin-reconsent"
                  checked={acceptedAdminToS}
                  onCheckedChange={(checked) => setAcceptedAdminToS(checked === true)}
                  className="mt-1"
                />
                <Label htmlFor="tos-admin-reconsent" className="text-sm leading-relaxed cursor-pointer flex-1">
                  I accept the updated{" "}
                  <Link href="/legal/terms-admin" className="text-primary hover:underline" target="_blank">
                    Administrator Terms of Service
                  </Link>
                  {" "}*
                </Label>
              </div>
            )}

            {needsReconsent.needsPrivacy && (
              <div className="flex items-start gap-3">
                <Checkbox
                  id="privacy-reconsent"
                  checked={acceptedPrivacy}
                  onCheckedChange={(checked) => setAcceptedPrivacy(checked === true)}
                  className="mt-1"
                />
                <Label htmlFor="privacy-reconsent" className="text-sm leading-relaxed cursor-pointer flex-1">
                  I accept the updated{" "}
                  <Link href="/legal/privacy" className="text-primary hover:underline" target="_blank">
                    Privacy Policy
                  </Link>
                  {" "}*
                </Label>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              onClick={handleAccept}
              disabled={updateConsentMutation.isPending}
              className="flex-1"
            >
              {updateConsentMutation.isPending ? "Updating..." : "Accept and Continue"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

